/**
 * Fase 6 — Vision-agent draft schemas.
 *
 * Two WRITE-tool input schemas (create_chore_draft, create_calendar_event_draft)
 * whose JSON Schema is fed to Anthropic via z.toJSONSchema(), plus the DTO shape
 * the /api/ai/drafts endpoint returns to the inline review cards.
 *
 * These schemas are the SINGLE source of truth shared by:
 *   - apps/api tools.registry / tools.handlers (Anthropic input_schema + parse)
 *   - apps/api drafts.service confirm path (re-validate the edited payload)
 *   - apps/web review cards (typed payload + edit form)
 *
 * Draft → human-confirm: the model only ever produces a `pending` row here; a
 * parent edits + confirms before anything reaches the live chores/calendar
 * tables. So the validators below double as the confirm-time payload guard.
 */
import { z } from 'zod';

/** Loose UUID regex — accepts both v4 and v7 (36-char canonical form). */
const UuidSchema = z.string().regex(/^[0-9a-f-]{36}$/i, 'invalid_uuid');

// ── Vision write-tool inputs ────────────────────────────────────────────────

export const CreateChoreDraftInputSchema = z.object({
  /** Lekse-/oppgavetittel, f.eks. "Matte s. 42–43". */
  title: z.string().min(1).max(120).describe('Tittel på leksen, f.eks. "Matte s. 42–43".'),
  /** Skolefag som grupperer leksen. */
  subject: z.string().max(60).optional().describe('Skolefag, f.eks. "Matematikk" eller "Norsk".'),
  /** Utfyllende beskrivelse hvis planen har detaljer. */
  description: z.string().max(500).optional().describe('Utfyllende beskrivelse hvis planen har detaljer.'),
  /** Foreslåtte poeng (0–100). */
  point_value: z.number().int().min(0).max(100).default(10).describe('Foreslåtte poeng (0–100). Standard 10.'),
  /** Innleveringsfrist som ISO-dato. */
  due_date: z
    .string()
    .optional()
    .describe('Innleveringsfrist som ISO-dato (YYYY-MM-DD). Utelat hvis planen ikke oppgir frist.'),
  /** Medlemmet planen tilhører — bruk barnet forelderen har valgt. */
  assignee_id: UuidSchema.optional().describe('Medlemmet planen tilhører. Bruk barnet forelderen har valgt.'),
});
export type CreateChoreDraftInput = z.infer<typeof CreateChoreDraftInputSchema>;

/**
 * Wave 3 — meal-planner draft. A parent asks the assistant to plan dinners (or
 * uploads a menu photo); the model proposes one draft per meal. Confirming a
 * meal persists it AND pushes its still-missing ingredients onto the shared
 * shopping list (the diff is computed server-side at confirm).
 *
 * Safety-critical (CLAUDE.md): unlike chore/event drafts which only soft-warn,
 * a meal draft whose title/ingredients/notes hit a household allergen is
 * HARD-BLOCKED at confirm — it cannot be committed without an explicit parent
 * override. Nut (Mamma) / egg (Bella) allergies are severe.
 */
export const CreateMealDraftInputSchema = z.object({
  /** Rett/måltid, f.eks. "Taco" eller "Fiskegrateng". */
  title: z.string().min(1).max(120).describe('Rett/måltid, f.eks. "Taco" eller "Fiskegrateng".'),
  /** Dato måltidet er planlagt for. */
  date: z
    .string()
    .optional()
    .describe('Dato som ISO (YYYY-MM-DD). Ukestart mandag. Utelat hvis planen ikke oppgir dag.'),
  /** Ingrediensene retten trenger — én streng per vare. */
  ingredients: z
    .array(z.string().min(1).max(120))
    .max(40)
    .default([])
    .describe('Ingredienser retten trenger. Én kort streng per vare, f.eks. "kjøttdeig" eller "taco-krydder".'),
  /** Valgfrie notater (tilbehør, antall personer). */
  notes: z.string().max(500).optional().describe('Valgfrie notater, f.eks. tilbehør eller antall personer.'),
});
export type CreateMealDraftInput = z.infer<typeof CreateMealDraftInputSchema>;

export const CreateCalendarEventDraftInputSchema = z.object({
  /** Tittel, f.eks. "Svømming" eller "Gym – ta med treningstøy". */
  title: z.string().min(1).max(120).describe('Tittel, f.eks. "Svømming" eller "Gym – ta med treningstøy".'),
  /** Utfyllende beskrivelse. */
  description: z.string().max(500).optional(),
  /** Starttidspunkt som ISO med Europe/Oslo-offset. */
  starts_at: z
    .string()
    .describe('Starttidspunkt som ISO (YYYY-MM-DDTHH:mm med Europe/Oslo-offset). For heldags: sett all_day og bruk midnatt.'),
  /** Sluttidspunkt som ISO. */
  ends_at: z.string().optional().describe('Sluttidspunkt som ISO. Utelat for åpne hendelser.'),
  /** true for heldagshendelser (turdag, utstyrsdag). */
  all_day: z.boolean().default(false).describe('true for heldagshendelser (turdag, utstyrsdag).'),
  /** Sted. */
  location: z.string().max(120).optional(),
  /** Medlemmet hendelsen gjelder — bruk barnet forelderen har valgt. */
  attendee_id: UuidSchema.optional().describe('Medlemmet hendelsen gjelder. Bruk barnet forelderen har valgt.'),
});
export type CreateCalendarEventDraftInput = z.infer<typeof CreateCalendarEventDraftInputSchema>;

// ── DTO returned by GET /api/ai/drafts ──────────────────────────────────────

/** One household-allergen hit found in a draft's text (safety-critical scan). */
export interface AllergenHit {
  /** Allergen-id that matched (e.g. 'nuts', 'egg'). */
  allergen: string;
  /** The Norwegian keyword root that triggered the hit (e.g. 'nøtt'). */
  keyword: string;
}

export type AiDraftKind = 'homework' | 'chore' | 'event' | 'meal';
export type AiDraftStatus = 'pending' | 'confirmed' | 'discarded';

/** Shape the inline review cards consume. `payload` is the editable proposal. */
export interface AiDraftDTO {
  id: string;
  batch_id: string;
  conversation_id: string;
  kind: AiDraftKind;
  payload: CreateChoreDraftInput | CreateCalendarEventDraftInput | CreateMealDraftInput;
  allergen_warning: AllergenHit[] | null;
  status: AiDraftStatus;
  result_ref_id: string | null;
  created_at: string;
}

// ── Batch: «Lagre N av M» og «Forkast alle» (D13, #163) ─────────────────────

/** Øvre grense for én batch. En ukeplan gir et titalls utkast, ikke hundre. */
export const DRAFT_BATCH_MAX = 50;

/**
 * Ett utkast i en batch-bekreftelse: den (eventuelt redigerte) nyttelasten og
 * forelderens eksplisitte «Jeg tar ansvar». Nyttelasten valideres per utkast
 * på serveren mot skjemaet for utkastets type, så den er `unknown` her.
 */
export const ConfirmDraftBatchItemSchema = z.object({
  id: z.string().min(1),
  payload: z.unknown(),
  override: z.boolean().optional(),
});
export type ConfirmDraftBatchItem = z.infer<typeof ConfirmDraftBatchItemSchema>;

/** POST /api/ai/drafts/confirm-batch. */
export const ConfirmDraftBatchRequestSchema = z.object({
  items: z
    .array(ConfirmDraftBatchItemSchema)
    .min(1)
    .max(DRAFT_BATCH_MAX)
    .refine((items) => new Set(items.map((i) => i.id)).size === items.length, 'duplicate_id'),
});
export type ConfirmDraftBatchRequest = z.infer<typeof ConfirmDraftBatchRequestSchema>;

/** POST /api/ai/drafts/discard-batch. */
export const DiscardDraftBatchRequestSchema = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(DRAFT_BATCH_MAX)
    .refine((ids) => new Set(ids).size === ids.length, 'duplicate_id'),
});
export type DiscardDraftBatchRequest = z.infer<typeof DiscardDraftBatchRequestSchema>;

/**
 * Et utkast batchen IKKE lagret eller forkastet. Det står fortsatt som
 * `pending` (med mindre `error` er `draft_not_pending`). `allergen_blocked`
 * betyr at serverens skanning sperret en middag uten overstyring.
 */
export interface DraftBatchSkipped {
  id: string;
  error: string;
}

/** Svaret fra begge batch-endepunktene: det som ble gjort, og det som ble liggende. */
export interface DraftBatchResult {
  done: AiDraftDTO[];
  skipped: DraftBatchSkipped[];
}
